/**
 * Rarity utilities for TasteInsights
 *
 * Rarity tier is assigned by the LLM during TasteInsights generation.
 * This module provides utility functions for styling (colors, display names, emojis).
 */

import type { TasteInsights, TasteInsightsFriend, RarityTier } from '@/types/tasteInsights'

// Re-export RarityTier for convenience
export type { RarityTier } from '@/types/tasteInsights'

export interface RarityColors {
  bg: string
  border: string
  glow: string
  textColor: string
  gradient: string
}

/**
 * Get the rarity tier from TasteInsights.
 * Returns the LLM-assigned tier, or 'common' if not available.
 */
export function getRarityTier(
  tasteInsights: TasteInsights | TasteInsightsFriend | null | undefined
): RarityTier {
  if (!tasteInsights || !tasteInsights.isReady || !tasteInsights.hasContent) {
    return 'common'
  }

  return tasteInsights.rarityTier ?? 'common'
}

/**
 * Get visual styling for a rarity tier
 */
export function getRarityColors(tier: RarityTier): RarityColors {
  switch (tier) {
    case 'ultra rare':
      return {
        bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
        border: '#FFD700',
        glow: '0 0 20px rgba(255, 215, 0, 0.6), 0 0 40px rgba(255, 165, 0, 0.4)',
        textColor: '#1A1A1A',
        gradient: 'linear-gradient(135deg, #FFD700, #FFA500)',
      }
    case 'super rare':
      return {
        bg: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 50%, #7D3C98 100%)',
        border: '#9B59B6',
        glow: '0 0 16px rgba(155, 89, 182, 0.5), 0 0 32px rgba(142, 68, 173, 0.3)',
        textColor: '#FFFFFF',
        gradient: 'linear-gradient(135deg, #9B59B6, #8E44AD)',
      }
    case 'rare':
      return {
        bg: 'linear-gradient(135deg, #3498DB 0%, #2980B9 50%, #1F618D 100%)',
        border: '#3498DB',
        glow: '0 0 12px rgba(52, 152, 219, 0.4), 0 0 24px rgba(41, 128, 185, 0.2)',
        textColor: '#FFFFFF',
        gradient: 'linear-gradient(135deg, #3498DB, #2980B9)',
      }
    case 'uncommon':
      return {
        bg: 'linear-gradient(135deg, #27AE60 0%, #229954 50%, #1E8449 100%)',
        border: '#27AE60',
        glow: '0 0 8px rgba(39, 174, 96, 0.3)',
        textColor: '#FFFFFF',
        gradient: 'linear-gradient(135deg, #27AE60, #229954)',
      }
    case 'common':
    default:
      return {
        bg: 'linear-gradient(135deg, #7F8C8D 0%, #6C7A7D 50%, #5D6D6E 100%)',
        border: '#7F8C8D',
        glow: 'none',
        textColor: '#FFFFFF',
        gradient: 'linear-gradient(135deg, #7F8C8D, #6C7A7D)',
      }
  }
}

/**
 * Get the display name for a rarity tier
 */
export function getRarityDisplayName(tier: RarityTier): string {
  // Capitalize each word
  return tier
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Get the icon/emoji for a rarity tier
 */
export function getRarityEmoji(tier: RarityTier): string {
  switch (tier) {
    case 'ultra rare':
      return '👑'
    case 'super rare':
      return '💎'
    case 'rare':
      return '⭐'
    case 'uncommon':
      return '✨'
    case 'common':
    default:
      return '🔹'
  }
}

/**
 * Check if a tier should have animated glow effect
 */
export function shouldAnimateGlow(tier: RarityTier): boolean {
  return tier === 'ultra rare' || tier === 'super rare'
}
